import React, { useEffect, useRef, useState } from 'react';

export type TableRowAction = {
  key: string;
  label: string;
  onClick: () => void | Promise<void>;
  icon?: string;
  variant?: 'default' | 'delete' | 'info';
  disabled?: boolean;
  loading?: boolean;
};

type TableRowActionsProps = {
  actions: TableRowAction[];
  maxVisible?: number;
  globalDisabled?: boolean;
  error?: string | null;
  overflowLabel: string;
};

const DEFAULT_MAX_VISIBLE = 3;

const actionClassName = (
  action: TableRowAction,
  baseClass: string,
  isLoading: boolean,
): string =>
  `${baseClass}${action.variant === 'delete' ? ` ${baseClass}--delete` : ''}${action.variant === 'info' ? ` ${baseClass}--info` : ''}${isLoading ? ' is-loading' : ''}`;

const ActionContent = ({ action }: { action: TableRowAction }) => (
  <>
    {action.icon !== undefined && action.variant !== 'info' ? (
      <img src={action.icon} alt="" width={20} height={20} aria-hidden />
    ) : null}
    <span>{action.label}</span>
  </>
);

const TableRowActions = ({
  actions,
  maxVisible = DEFAULT_MAX_VISIBLE,
  globalDisabled = false,
  error,
  overflowLabel,
}: TableRowActionsProps) => {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!overflowOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        overflowRef.current &&
        !overflowRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOverflowOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOverflowOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [overflowOpen]);

  const visible = actions.slice(0, Math.max(0, maxVisible));
  const overflow = actions.slice(Math.max(0, maxVisible));

  const runAction = (action: TableRowAction) => {
    if (globalDisabled || action.disabled === true || action.loading === true) {
      return;
    }
    void action.onClick();
  };

  const renderAction = (action: TableRowAction) => {
    const isLoading = action.loading === true;
    const isDisabled = globalDisabled || action.disabled === true || isLoading;

    return (
      <button
        key={action.key}
        type="button"
        className={actionClassName(action, 'table-row-actions__link', isLoading)}
        disabled={isDisabled}
        onClick={() => runAction(action)}
      >
        <ActionContent action={action} />
      </button>
    );
  };

  if (actions.length === 0 && !error) {
    return null;
  }

  return (
    <div className="table-row-actions">
      <div className="table-row-actions__links">
        {visible.map((action) => renderAction(action))}
      </div>
      {overflow.length > 0 ? (
        <div className="table-row-actions__overflow">
          <button
            ref={buttonRef}
            type="button"
            className={`table-row-actions__overflow-btn${overflowOpen ? ' is-open' : ''}`}
            aria-label={overflowLabel}
            aria-expanded={overflowOpen}
            disabled={globalDisabled}
            onClick={() => setOverflowOpen((open) => !open)}
          >
            <span aria-hidden>⋮</span>
          </button>
          {overflowOpen ? (
            <div ref={overflowRef} className="table-row-actions__overflow-panel">
              {overflow.map((action) => {
                const isLoading = action.loading === true;
                const isDisabled = globalDisabled || action.disabled === true || isLoading;

                return (
                  <button
                    key={action.key}
                    type="button"
                    className={actionClassName(action, 'table-row-actions__overflow-item', isLoading)}
                    disabled={isDisabled}
                    onClick={() => {
                      setOverflowOpen(false);
                      runAction(action);
                    }}
                  >
                    <ActionContent action={action} />
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
      {error ? <div className="table-row-actions__error">{error}</div> : null}
    </div>
  );
};

export default TableRowActions;
